import { StrataCooldownBalanceAbi } from '../abis/strataCooldown'
import type { LstWithdrawalRequest } from '../types'
import { toBigInt, toNumber, type LstWithdrawalReader } from './shared'

/**
 * Strata per-market cooldown reader.
 *
 * A tranche exit escrows into one of the market's two `ICooldown`
 * contracts — the `UnstakeCooldown` (`entry.withdrawalContract`, the
 * base-asset redeem leg that unstakes the collateral) or the
 * `ERC20Cooldown` (`entry.secondaryWithdrawalContract`, the
 * collateral-token leg, 0-second cooldown at integration). Both book
 * requests per `(escrowToken, user)` with NO tranche attribution — a
 * senior and a junior exit land in the same slot — so the registry
 * carries ONE entry per market (keyed on the senior tranche address for
 * catalog resolution) rather than one per tranche, and this reader
 * reports the market-level aggregate.
 *
 * One `balanceOf(escrowToken, user)` per contract →
 * `{pending, claimable, nextUnlockAt, nextUnlockAmount, totalRequests}`.
 * The claim — `finalize(escrowToken, user)` via calldata-sdk's `strata`
 * savings kind — pays out every unlocked request on that contract at
 * once.
 *
 * Denomination trap: the escrow is **keyed** by the collateral token but
 * the amount it records is what that leg settles in — the tranche's
 * `asset()` on the UnstakeCooldown (it books Ethena's unstake output;
 * fork-verified 2026-08-04: 10,000 USDe in → 9,997.5 USDe out, no sUSDe)
 * and collateral-token shares on the ERC20Cooldown. So the key does NOT
 * imply the denomination, and we deliberately do not normalize —
 * `withdrawQueue` on each request says which leg it came from.
 */
export const readerStrataCooldown: LstWithdrawalReader = {
  fetch: async (user, multicallRetry, chainId, entry) => {
    const escrowToken = entry.escrowToken
    if (!escrowToken) return []
    const contracts = [
      entry.withdrawalContract,
      entry.secondaryWithdrawalContract,
    ].filter((c): c is string => !!c)
    if (contracts.length === 0) return []

    const res = (await multicallRetry({
      chain: chainId,
      calls: contracts.map((address) => ({
        address,
        name: 'balanceOf',
        params: [escrowToken, user],
      })) as unknown as never[],
      abi: contracts.map(() => StrataCooldownBalanceAbi) as unknown as never,
    })) as unknown[]

    const out: LstWithdrawalRequest[] = []
    for (let i = 0; i < contracts.length; i++) {
      const cell = res[i] as
        | unknown[]
        | {
            pending?: unknown
            claimable?: unknown
            nextUnlockAt?: unknown
          }
        | undefined
      let pending: bigint | undefined
      let claimable: bigint | undefined
      let nextUnlockAt: number | undefined
      if (Array.isArray(cell)) {
        pending = toBigInt(cell[0])
        claimable = toBigInt(cell[1])
        nextUnlockAt = toNumber(cell[2])
      } else if (cell && typeof cell === 'object') {
        pending = toBigInt(cell.pending)
        claimable = toBigInt(cell.claimable)
        nextUnlockAt = toNumber(cell.nextUnlockAt)
      }

      // Which escrow the request sits on is load-bearing for the claim:
      // `finalize` only settles requests on the contract it is called
      // against, so a claim aimed at the other leg is a silent no-op.
      const escrow = { withdrawQueue: contracts[i], claimToken: escrowToken }

      if (claimable && claimable > 0n) {
        out.push({
          lst: entry.lst,
          brand: entry.brand,
          symbol: entry.symbol,
          requestId: `${i * 2}`,
          amountUnderlying: claimable.toString(),
          status: 'claimable',
          ...escrow,
        })
      }
      if (pending && pending > 0n) {
        out.push({
          lst: entry.lst,
          brand: entry.brand,
          symbol: entry.symbol,
          requestId: `${i * 2 + 1}`,
          amountUnderlying: pending.toString(),
          status: 'pending',
          ...(nextUnlockAt ? { readyAt: nextUnlockAt } : {}),
          ...escrow,
        })
      }
    }
    return out
  },
}
